"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getApr = getApr;
exports.getAprs = getAprs;
exports.getRewardAprs = getRewardAprs;
exports.getApy = getApy;
exports.getApys = getApys;
exports.fetchRewardAPR = fetchRewardAPR;
const tslib_1 = require("tslib");
const fetchAutoCompoundingEvents_js_1 = require("./sui-sdk/events/fetchAutoCompoundingEvents.js");
const maps_js_1 = require("./common/maps.js");
const decimal_js_1 = require("decimal.js");
const constants_js_1 = require("./common/constants.js");
const getReceipts_js_1 = require("./sui-sdk/functions/getReceipts.js");
const prices_js_1 = require("./utils/prices.js");
const cetus_sui_clmm_sdk_1 = require("@cetusprotocol/cetus-sui-clmm-sdk");
const bn_js_1 = tslib_1.__importDefault(require("bn.js"));
const coins_js_1 = require("./common/coins.js");
async function getApr(poolName) {
    const aprMap = await getAprs([poolName]);
    const apr = aprMap[poolName];
    return apr;
}
async function getAprs(poolNames) {
    const endTime = Date.now();
    const startTime = endTime - 24 * 60 * 60 * 1000; // timestamp for 24 hours ago
    const events = await (0, fetchAutoCompoundingEvents_js_1.fetchAutoCompoundingEvents)({
        startTime: startTime,
        endTime: endTime,
        poolNames: poolNames,
    });
    const aprMap = await (0, fetchAutoCompoundingEvents_js_1.calculateAprForPools)(events);
    for (const pool of Object.keys(maps_js_1.poolInfo)) {
        const poolName = pool;
        if (poolName === "BLUEFIN-AUTOBALANCE-WAL-USDC") {
            aprMap[poolName] = 17.14;
        }
        else if (poolName === "BLUEFIN-AUTOBALANCE-DEEP-SUI") {
            aprMap[poolName] = 11.36;
        }
        else if (poolName === "BLUEFIN-AUTOBALANCE-SUIUSDT-USDC-ZERO-ZERO") {
            aprMap[poolName] = 15.51;
        }
        else if (poolName === "BLUEFIN-AUTOBALANCE-SUIUSDT-USDC") {
            aprMap[poolName] = 15.14;
        }
        else if (poolName === "BLUEFIN-AUTOBALANCE-DEEP-BLUE") {
            aprMap[poolName] = 14.76;
        }
        else if (poolName === "BLUEFIN-AUTOBALANCE-BLUE-SUI") {
            aprMap[poolName] = 15.18;
        }
        else if (poolName === "BLUEFIN-AUTOBALANCE-USDT-USDC") {
            aprMap[poolName] = 7.72;
        }
        else if (!(poolName in aprMap)) {
            aprMap[poolName] = 0;
        }
    }
    return aprMap;
}
async function getRewardAprs(poolNames) {
    await Promise.all([
        (0, getReceipts_js_1.getMultiPool)(),
        (0, getReceipts_js_1.getMultiCetusPool)(),
        (0, getReceipts_js_1.getMultiInvestor)(),
        (0, getReceipts_js_1.getMultiParentPool)(),
        (0, prices_js_1.getMultiLatestPrices)(),
    ]);
    const aprMap = {};
    if (poolNames) {
        poolNames.forEach(async (pool) => {
            aprMap[pool] = await fetchRewardAPR(pool, false);
        });
    }
    else {
        for (const pool of Object.keys(maps_js_1.poolInfo)) {
            aprMap[pool] = await fetchRewardAPR(pool, false);
        }
    }
    return aprMap;
}
async function getApy(poolName) {
    const apy = convertAprToApy(await getApr(poolName));
    return apy;
}
async function getApys(poolNames) {
    const aprMap = await getAprs(poolNames);
    const rewardAprMap = await getRewardAprs(poolNames);
    // console.log(aprMap);
    // Convert each APR to APY
    const apyMap = {};
    for (const poolName in aprMap) {
        if (aprMap.hasOwnProperty(poolName)) {
            apyMap[poolName] = convertAprToApy(aprMap[poolName] + rewardAprMap[poolName]);
        }
    }
    return apyMap;
}
/**
 * Converts APR to APY with compounding 6 times a day
 * @param apr - The annual percentage rate (APR) as a decimal
 * @returns The annual percentage yield (APY) as a decimal
 */
function convertAprToApy(apr) {
    const n = 6 * 365; // 6 times a day
    const apy = 100 * (Math.pow(1 + apr / 100 / n, n) - 1);
    return apy;
}
async function fetchRewardAPR(poolName, ignoreCache) {
    let apr = 0;
    if (!(poolName in maps_js_1.poolInfo)) {
        return 0;
    }
    try {
        if (poolName === "ALPHA") {
            const pool = await (0, getReceipts_js_1.getPool)("ALPHA", ignoreCache);
            const distributor = await (0, getReceipts_js_1.getDistributor)(ignoreCache);
            if (pool && distributor) {
                const allocator = distributor.content.fields.pool_allocator;
                const tokensInvested = Number(pool.content.fields.tokensInvested);
                const target = Number(distributor.content.fields.target);
                const members = allocator.fields.members.fields.contents;
                let alphaMember = undefined;
                for (const member of members) {
                    if (member.fields.key === (0, constants_js_1.getConf)().ALPHA_POOL) {
                        alphaMember = member;
                    }
                }
                if (alphaMember) {
                    const weight = Number(alphaMember.fields.value.fields.pool_data.fields.contents[0].fields
                        .value.fields.weight);
                    const totalWeightArr = allocator.fields.total_weights.fields.contents;
                    const totalWeight = Number(totalWeightArr[0].fields.value);
                    if (totalWeight === 0 || tokensInvested === 0)
                        return 0;
                    apr =
                        (((target / 100) * 365 * weight) / totalWeight / tokensInvested) *
                            (2750000 / 4250000) *
                            100;
                }
            }
        }
        else if (Object.keys(maps_js_1.doubleAssetPoolCoinMap).includes(poolName)) {
            const distributor = await (0, getReceipts_js_1.getDistributor)(ignoreCache);
            let clmmPool;
            if (maps_js_1.poolInfo[poolName].parentProtocolName == "CETUS") {
                clmmPool = await (0, getReceipts_js_1.getCetusPool)(poolName, ignoreCache);
            }
            else {
                clmmPool = await (0, getReceipts_js_1.getParentPool)(poolName, ignoreCache);
            }
            const pool = await (0, getReceipts_js_1.getPool)(poolName, ignoreCache);
            let investor;
            if (maps_js_1.poolInfo[poolName].parentProtocolName == "CETUS") {
                investor = (await (0, getReceipts_js_1.getInvestor)(poolName, ignoreCache));
            }
            else {
                investor = (await (0, getReceipts_js_1.getInvestor)(poolName, ignoreCache));
            }
            const tokenA = maps_js_1.doubleAssetPoolCoinMap[poolName].coin1;
            const tokenB = maps_js_1.doubleAssetPoolCoinMap[poolName].coin2;
            if (distributor && clmmPool && investor && pool) {
                const allocator = distributor.content.fields.pool_allocator;
                const members = allocator.fields.members.fields.contents;
                let poolMember = undefined;
                for (const member of members) {
                    if (member.fields.key == maps_js_1.poolInfo[poolName].poolId) {
                        poolMember = member;
                    }
                }
                if (poolMember) {
                    try {
                        const target = Number(distributor.content.fields.target);
                        const weight = Number(poolMember.fields.value.fields.pool_data.fields.contents[0].fields
                            .value.fields.weight);
                        const totalWeight = Number(allocator.fields.total_weights.fields.contents[0].fields.value);
                        if (totalWeight === 0)
                            return 0;
                        const [priceOfCoin0, priceOfCoin1] = await (0, prices_js_1.getLatestPrices)([
                            `${tokenA}/USD`,
                            `${tokenB}/USD`,
                        ], ignoreCache);
                        const [priceOfAlpha] = await (0, prices_js_1.getLatestPrices)(["ALPHA/USD"], ignoreCache);
                        if (priceOfCoin0 && priceOfCoin1) {
                            const liquidity = Math.floor(parseFloat(pool.content.fields.tokensInvested)).toString();
                            const current_sqrt_price = new bn_js_1.default(clmmPool.content.fields.current_sqrt_price);
                            const upperBound = 443636;
                            let lowerTick = Number(investor.content.fields.lower_tick);
                            let upperTick = Number(investor.content.fields.upper_tick);
                            if (lowerTick > upperBound) {
                                lowerTick = -~(lowerTick - 1);
                            }
                            if (upperTick > upperBound) {
                                upperTick = -~(upperTick - 1);
                            }
                            const upper_sqrt_price = cetus_sui_clmm_sdk_1.TickMath.tickIndexToSqrtPriceX64(upperTick);
                            const lower_sqrt_price = cetus_sui_clmm_sdk_1.TickMath.tickIndexToSqrtPriceX64(lowerTick);
                            const { coinA, coinB } = cetus_sui_clmm_sdk_1.ClmmPoolUtil.getCoinAmountFromLiquidity(new bn_js_1.default(liquidity), current_sqrt_price, lower_sqrt_price, upper_sqrt_price, false);
                            let amount0 = coinA;
                            let amount1 = coinB;
                            // amount0 = amount0 / 10 ** coins[tokenA as CoinName].expo;
                            // amount1 = amount1 / 10 ** coins[tokenB as CoinName].expo;
                            amount0 = amount0.div(new bn_js_1.default(10 ** coins_js_1.coinsList[tokenA].expo));
                            amount1 = amount1.div(new bn_js_1.default(10 ** coins_js_1.coinsList[tokenB].expo));
                            if (priceOfAlpha) {
                                const amount0Big = new decimal_js_1.Decimal(amount0.toString());
                                const targetBig = new decimal_js_1.Decimal(target);
                                const weightBig = new decimal_js_1.Decimal(weight);
                                const totalWeightBig = new decimal_js_1.Decimal(totalWeight);
                                const priceOfAlphaBig = new decimal_js_1.Decimal(priceOfAlpha);
                                const priceOfCoin0Big = new decimal_js_1.Decimal(priceOfCoin0);
                                const amount1Big = new decimal_js_1.Decimal(amount1.toString());
                                const priceOfCoin1Big = new decimal_js_1.Decimal(priceOfCoin1);
                                if (totalWeight === 0 ||
                                    amount0Big
                                        .mul(priceOfCoin0Big)
                                        .add(amount1Big.mul(priceOfCoin1Big))
                                        .isZero()) {
                                    return 0;
                                }
                                // apr =
                                //   (((((target / (100 * 1_000_000_000)) * 365 * weight) /
                                //     totalWeight) *
                                //     parseFloat(priceOfAlpha)) /
                                //     (amount0 * Number(priceOfCoin0) +
                                //       amount1 * Number(priceOfCoin1))) *
                                //   (2_750_000 / 4_250_000) *
                                //   100;
                                apr = targetBig
                                    .div(new decimal_js_1.Decimal(100).mul(new decimal_js_1.Decimal(1000000000)))
                                    .mul(new decimal_js_1.Decimal(365))
                                    .mul(weightBig)
                                    .div(totalWeightBig)
                                    .mul(priceOfAlphaBig)
                                    .div(amount0Big
                                    .mul(priceOfCoin0Big)
                                    .add(amount1Big.mul(priceOfCoin1Big)))
                                    .mul(new decimal_js_1.Decimal(2750000).div(new decimal_js_1.Decimal(4250000)))
                                    .mul(new decimal_js_1.Decimal(100))
                                    .toNumber();
                            }
                        }
                    }
                    catch (error) {
                        console.log(`Error calculating Alpha APR for pool ${poolName}`, error);
                    }
                }
            }
        }
        else if (Object.keys(maps_js_1.singleAssetPoolCoinMap).includes(poolName)) {
            const distributor = await (0, getReceipts_js_1.getDistributor)(ignoreCache);
            const pool = await (0, getReceipts_js_1.getPool)(poolName, ignoreCache);
            const token = maps_js_1.singleAssetPoolCoinMap[poolName];
            if (distributor && pool) {
                const allocator = distributor.content.fields.pool_allocator;
                const members = allocator.fields.members.fields.contents;
                let poolMember = undefined;
                for (const member of members) {
                    if (member.fields.key == maps_js_1.poolInfo[poolName].poolId) {
                        poolMember = member;
                    }
                }
                if (poolMember) {
                    try {
                        const target = Number(distributor.content.fields.target);
                        const weight = Number(poolMember.fields.value.fields.pool_data.fields.contents[0].fields
                            .value.fields.weight);
                        const totalWeight = Number(allocator.fields.total_weights.fields.contents[0].fields.value);
                        if (totalWeight === 0)
                            return 0;
                        const [priceOfCoin] = await (0, prices_js_1.getLatestPrices)([`${token.coin}/USD`], ignoreCache);
                        const [priceOfAlpha] = await (0, prices_js_1.getLatestPrices)(["ALPHA/USD"], ignoreCache);
                        if (priceOfCoin) {
                            let tokensInvested = new decimal_js_1.Decimal(pool.content.fields.tokensInvested);
                            // dividing by 1e9 for every token because navi scales every token's liquidity to 1e9;
                            if (poolName == "NAVI-LOOP-SUI-VSUI" ||
                                poolName == "NAVI-LOOP-USDC-USDT" ||
                                poolName == "NAVI-LOOP-HASUI-SUI" ||
                                poolName == "NAVI-LOOP-USDT-USDC") {
                                const investor = (await (0, getReceipts_js_1.getInvestor)(poolName, ignoreCache));
                                if (investor) {
                                    const debtToSupplyRatio = new decimal_js_1.Decimal(investor.content.fields.current_debt_to_supply_ratio);
                                    tokensInvested = tokensInvested
                                        .mul(new decimal_js_1.Decimal(1).minus(debtToSupplyRatio.div(new decimal_js_1.Decimal(1e20))))
                                        .div(new decimal_js_1.Decimal(1e9));
                                }
                                else {
                                    tokensInvested = new decimal_js_1.Decimal(0);
                                }
                            }
                            else {
                                tokensInvested = tokensInvested.div(Math.pow(10, 9));
                            }
                            const liquidity = tokensInvested.toNumber();
                            if (priceOfAlpha) {
                                if (totalWeight === 0 || liquidity * Number(priceOfCoin) === 0)
                                    return 0;
                                apr =
                                    (((((target / (100 * 1000000000)) * 365 * weight) /
                                        totalWeight) *
                                        parseFloat(priceOfAlpha)) /
                                        (liquidity * Number(priceOfCoin))) *
                                        (2750000 / 4250000) *
                                        100;
                            }
                        }
                    }
                    catch (e) {
                        console.error("error in fetchRewardAPR", e);
                    }
                }
            }
        }
        else {
            console.log("fetchRewardAPR not implemented for poolName: " + poolName);
        }
    }
    catch (e) {
        console.error("error in fetchRewardAPR", e);
    }
    if (!apr) {
        apr = 0;
    }
    return apr;
}
//# sourceMappingURL=getAprs.js.map